import { EventedCallbackOrArray } from '@dojo/framework/core/Evented';
import { publish } from 'pub-sub';
import defer from 'lodash-es/defer';
import delay from 'lodash-es/delay';
import Queue from 'collections/Queue';
import { logSystem } from 'logger';

import WebSocket, { SendState } from './WebSocket';
import { Defer, createDefer } from './_defer';

import './eventtransport';

// WebSocket traffic is wrapped in an envelope that indicates the
// type of message and any necessary information needed to route
// the message. The envelope is a simple array. On the wire, all
// traffic is JSON-encoded, so only strings are involved.

/**
 * Action determines how a WS message is dispatched.
 *
 * @export
 * @enum {number}
 */
export enum Action {
	PING,
	EVENT,
	JSONRPC,
	CDPPUSH,
	MANHOLE,
	SESSID
}

export interface CBQueue {
	[index: string]: any;
}

export interface WSDispatcherOptions {
	url: string;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Envelope {
	action: Action;
	id: any;
	data: Record<string, any> | string;
}

export interface WSMessageEvent extends Event {
	data: string;
}

const TIMEOUT_BASE: number = 15;
const TIMEOUT_STEP: number[] = [0, 0, 0, 0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233];

interface WSDispatcherProperties {
	cbqueue: CBQueue;
	closing: boolean;
	msg_queue: Queue<any>;
	nextid: number;
	ping_hnd: any;
	queue_available: Defer<boolean> | null;
	reopen_count: number;
	url: string;
	ws: WebSocket;
}

const propsmap = new WeakMap<WSDispatcher, WSDispatcherProperties>();

/**
 * WebSocket dispatcher
 *
 * Message dispatch for WebSocket: Messages are dispatched based on
 * an action type. If the WebSocket isn't open, messages are queued
 * and then sent when the WebSocket connects.
 *
 * @class WSDispatcher
 */
export class WSDispatcher {
	/**
	 * Creates an instance of WSDispatcher.
	 *
	 * @param {IWSDispatcherOptions} options
	 *
	 * @memberOf WSDispatcher
	 */
	constructor(options: WSDispatcherOptions) {
		let props = propsmap.get(this) || ({} as WSDispatcherProperties);

		const { url } = options;
		props.url = url;
		props.queue_available = null;
		props.closing = false;
		propsmap.set(this, props);

		this._acquire_queue();
		props = propsmap.get(this) as WSDispatcherProperties;
		props.cbqueue = {};
		props.nextid = 1;
		props.msg_queue = new Queue<any>();
		props.reopen_count = 0;
		propsmap.set(this, props);
		this._release_queue();
		props = propsmap.get(this) as WSDispatcherProperties;
		const ws = (props.ws = new WebSocket(this.url));
		propsmap.set(this, props);

		ws.on('message', this._msgdispatch.bind(this) as EventedCallbackOrArray);
		ws.on('open', () => {
			(propsmap.get(this) as WSDispatcherProperties).reopen_count = 0;
			this._flush_queue();
		});
		ws.on('close', () => {
			this._reopen();
		});
	}

	private get props(): WSDispatcherProperties {
		return propsmap.get(this) as WSDispatcherProperties;
	}

	get url(): string {
		return (propsmap.get(this) as WSDispatcherProperties).url;
	}

	open(force: boolean = false): void {
		const props = this.props;
		const { ws } = props;
		props.closing = false;
		propsmap.set(this, props);
		ws.open(force);
	}

	close(): void {
		const props = this.props;
		const { ws } = props;
		props.closing = true;
		propsmap.set(this, props);
		ws.close();
	}

	on(evtname: string, action: EventedCallbackOrArray): any {
		const { ws } = this.props;
		return ws.on(evtname, action);
	}

	is_open(): boolean {
		const { ws } = this.props;
		return ws.is_open();
	}

	private _reopen(): void {
		const props = this.props;
		const { closing, reopen_count } = props;
		if (closing) {
			return;
		}
		const tmo = Math.min(reopen_count, TIMEOUT_STEP.length - 1);
		const timeout = TIMEOUT_STEP[tmo] + TIMEOUT_BASE;
		props.reopen_count++;
		propsmap.set(this, props);
		setTimeout((): void => {
			this.open();
		}, timeout * 1000);
	}

	private _acquire_queue(): void {
		const props = this.props;
		props.queue_available = createDefer();
		propsmap.set(this, props);
	}

	private _release_queue(): void {
		const props = this.props;
		props.queue_available && props.queue_available.resolve(true);
		propsmap.set(this, props);
	}

	private _msgdispatch(msg: WSMessageEvent): void {
		const parsedmsg: any[] = JSON.parse(msg.data);
		const act: Action = parsedmsg[0] as number;
		switch (act) {
			case Action.PING:
				this._do_ping(parsedmsg);
				break;
			case Action.EVENT:
				this._do_event(parsedmsg);
				break;
			case Action.JSONRPC:
				this._do_jsonrpc(parsedmsg);
				break;
			case Action.CDPPUSH:
				this._do_cdppush(parsedmsg);
				break;
			case Action.MANHOLE:
				this._do_manhole(parsedmsg);
				break;
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private _do_ping(msg: any[]) {
		const props = this.props;
		const { ping_hnd } = props;
		if (ping_hnd) {
			clearTimeout(ping_hnd);
		}
		props.ping_hnd = setTimeout(() => {
			props.ping_hnd = null;
			this.close();
			setTimeout(() => {
				props.closing = false;
				propsmap.set(this, props);
				this._reopen();
			}, 2000);
		}, 40000);
		propsmap.set(this, props);
	}

	private _do_event(msg: any[]) {
		const evtobj = msg[3];
		const evt = evtobj.event;
		const args = evtobj.params;
		logSystem.debug({ msg: 'event =>', data: evtobj });
		publish(evt, args);
	}

	private _do_jsonrpc(msg: any[]) {
		const qid = msg[1];
		const headers = msg[2];
		const data = JSON.parse(msg[3]);
		const { cbqueue } = this.props;
		const p: Defer<any> = cbqueue[qid];
		if (p) {
			if (p.returnResponseObject) {
				const response = {
					content: data,
					headers: new Map<string, any>()
				};
				Object.keys(headers).forEach((k) => {
					response.headers.set(k.toLowerCase(), headers[k]);
				});
				p.resolve(response);
			} else {
				p.resolve(data);
			}
			delete cbqueue[qid];
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private _do_cdppush(msg: any[]) {
		// const data: any = msg[3];
	}

	private _do_manhole(msg: any[]) {
		const data: any = msg[3];
		publish('manhole:response', data.message);
	}

	private _add_back(message: any): Promise<any> {
		const { queue_available, msg_queue } = this.props;
		return (queue_available as Defer<boolean>).then((): void => {
			msg_queue.enqueue(message);
		});
	}

	private _send(): void {
		const { ws, msg_queue: mq, queue_available } = this.props;
		let timeout: number = 0;
		if (!(queue_available as Defer<boolean>).isResolved || !ws.is_open() || mq.isEmpty()) {
			return;
		}
		this._acquire_queue();
		const message = mq.peek();
		const sendokay = ws.send(JSON.stringify(message));
		if (sendokay === SendState.OKAY) {
			mq.dequeue();
		}
		this._release_queue();
		if (!mq.isEmpty() && (sendokay === SendState.OKAY || sendokay === SendState.BUFFERING)) {
			timeout = sendokay === SendState.OKAY ? 0 : 50;
			delay((): void => this._send(), timeout);
		}
	}

	private _flush_queue(): void {
		// Send any queued messages.
		let msg: any[];
		const { ws, queue_available } = this.props;
		queue_available &&
			queue_available.then((): void => {
				this._acquire_queue();
				const sessionid = window.hcstatic.sessionid;
				if (sessionid) {
					msg = [Action.SESSID, 0, null, { sessionid: sessionid }];
					ws.send(JSON.stringify(msg));
				}
				this._release_queue();
				defer((): void => this._send());
			});
	}

	send(message: any): void {
		// Queue the message and fire the sender.
		this._add_back(message).then(() => this._send());
	}

	send_ping(message: any): void {
		this.send([Action.PING, 0, null, message]);
	}

	send_event(message: any): void {
		logSystem.info(`event <= ${message}`);
		this.send([Action.EVENT, 0, null, message]);
	}

	send_jsonrpc(path: string, message: string, returnResponseObject: boolean = false): Defer<any> {
		logSystem.debug(() => {
			const loglen = Math.min(message.length, 200);
			const ellipsis = loglen < message.length ? '...' : '';
			return `${path} <= ${message.slice(0, loglen)}${ellipsis}`;
		});
		const props = this.props;
		const msg = [Action.JSONRPC, props.nextid++, path, message];
		const d = createDefer();
		d.returnResponseObject = returnResponseObject;
		props.cbqueue[msg[1]] = d;
		this.send(msg);
		propsmap.set(this, props);
		return d;
	}

	send_cdppush(message: string) {
		this.send([Action.CDPPUSH, 0, null, message]);
	}

	send_manhole(message: string) {
		this.send([Action.MANHOLE, 0, null, message + '\n']);
	}

	simulate(action: 'disconnect' | 'connect') {
		const props = this.props;
		if (action === 'disconnect') {
			!props.closing && this.close();
		} else {
			!this.is_open() && this.open();
		}
	}
}

export default WSDispatcher;
